import type { SignInOTPRequest } from '@/types';

import React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import { GoToSignUpButton } from '@/app/auth/sign-in/_components';
import { Box, Button, PinInput, Stack } from '@/components';
import {
  useSetUpOtp,
  useSignInOtp,
  useSignInVerifyOtp,
  useTimer,
  useTranslation,
} from '@/hooks';
import useIsAdmin from '@/hooks/store/useIsAdmin';
import { routes } from '@/statics';
import { isValidOtp, setCookie, setLocalStorage } from '@/utils';

const CodeLoginCodeForm: React.FC<SignInOTPRequest> = ({
  mobile,
  country_code_prefix,
}) => {
  const searchParams = useSearchParams();

  const [navigateLoading, setNavigateLoading] = React.useState<boolean>(false);
  const toggleUserType = useIsAdmin(
    useShallow((state) => state.toggleUserType)
  );

  const { t } = useTranslation('pages.signIn.form.codeLoginForm.codeForm');

  const { counter, setCounter } = useTimer(120);
  const [value, setValue] = React.useState('');

  const { mutateAsync: verify, isPending: verifyLoading } =
    useSignInVerifyOtp();
  const { mutateAsync: getOtp, isPending: getOtpLoading } = useSignInOtp();

  const router = useRouter();

  const onResend = () => {
    setValue('');
    void getOtp({ mobile, country_code_prefix }).then(() => setCounter(120));
  };
  const onSubmit = (valuePinInput: string, number: number): any => {
    if (isValidOtp({ value: valuePinInput, number })) {
      setNavigateLoading(true);

      void verify({
        code: Number(valuePinInput),
        mobile,
        country_code_prefix,
      })
        .then(async ({ token }) => {
          await setCookie({ name: 'lia-token', value: token });
          setLocalStorage({ name: 'lia-token', value: token });
          toggleUserType(false);
          router.replace(searchParams.get('returnURL') || routes.dashboard.url);
        })
        .catch(() => setNavigateLoading(false));
    }
  };

  const MemoizedOnOtpReceived = React.useCallback((otpValue: string) => {
    setValue(otpValue);
    void onSubmit(otpValue, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSetUpOtp(MemoizedOnOtpReceived);
  return (
    <Stack as="form" w="full" pt={4} flex={1}>
      <Stack w="full" pt={4} flex={1}>
        <Box>
          <PinInput
            numberOfPinInputField={6}
            setValuePinInput={setValue}
            onComplete={onSubmit}
            value={value}
          />
        </Box>
        <Stack
          w="100%"
          flexDir={{ base: 'column', md: 'row' }}
          align="center"
          justify={{ base: 'flex-end', md: 'space-between' }}
          pt={4}
          gap={{ base: 4, md: 0 }}
        >
          <Button
            variant="link"
            isDisabled={counter !== 0}
            onClick={onResend}
            isLoading={getOtpLoading}
          >
            {`${t('resendCode')} : 0${Math.floor(counter / 60)}:${counter % 60 < 10 ? '0' : ''}${counter % 60}`}
          </Button>
        </Stack>
      </Stack>
      <Stack w="full" align="center" gap={6} justify="end" py={4} flexGrow={1}>
        <Button
          w="full"
          type="submit"
          onClick={() => void onSubmit(value, 6)}
          isLoading={verifyLoading || navigateLoading}
        >
          {t('submitCode')}
        </Button>
        <GoToSignUpButton />
      </Stack>
    </Stack>
  );
};

export default CodeLoginCodeForm;
